import { FormStatusEnum } from "@/components/molecules/FormWrapper/FormWrapper.types";
import { useEffect, useState } from "react";
import { LimitTypeEnum, OrderStatusEnum, OrderTypeEnum, Share, VariableOrderEntity } from "../../modules/orders/domain/entities/OrdersEntity";
import { useQueryClient } from "@tanstack/react-query";
import { useTitle } from "@/core/context/TitleContext";
import { useVariableOrdersData } from "../../modules/orders/application/hooks/useVariableOrdersData";
import { Loading } from "@/components/atoms/Loading/Loading";
import { FormHeader } from "@/components/molecules/FormHeader/FormHeader";
import { DataTable } from "@/components/molecules/DataTable/DataTable";
import { VariableOrdersForm } from "../../modules/orders/presentation/VariableOrdersForm";
import { useConfirmDialogs } from "@/core/context/DialogContext";
import { columns, VariableOrderFormData, variableOrderKeys } from "@/modules/orders/infrastructure/VariableOrdersConstants";
import { useCreateVariableOrder, useDeleteVariableOrder, useUpdateVariableOrder } from "@/modules/orders/application/hooks/useOrdersQueries";
import { CopyIcon } from "lucide-react";
import { ButtonBase } from "@/components/ui/button";

// Componente funcional
export const VariableOrdersPage = () => {
    const [formStatus, setFormStatus] = useState<FormStatusEnum>(FormStatusEnum.List);
    const [instrumentSelected, setInstrumentSelected] = useState<Share>();
    const [orderSelected, setOrderSelected] = useState<VariableOrderEntity>();
    const [, setFormKey] = useState(0);
    const { setTitle } = useTitle();
    const showDialog = useConfirmDialogs();

    const queryClient = useQueryClient();
    const {
        orders,
        portfolios,
        portfolioOptions,
        instrumentOptions,
        instruments,
        isLoading,
        ordersRefetch,
    } = useVariableOrdersData();

    // effects
    useEffect(() => {
        setTitle("Ordenes");
    }, []);

    useEffect(() => {
        orderSelected?.instrument && setInstrumentSelected(orderSelected.instrument);
    }, [orderSelected]);

    // hooks
    const createVariableOrderMutation = useCreateVariableOrder();
    const updateOrderMutation = useUpdateVariableOrder();
    const deleteOrderMutation = useDeleteVariableOrder();

    const onChange = (id: string) => {
        setInstrumentSelected(instruments.find(instrument => instrument.id === id));
    }

    const handleCopy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
        } catch (err) {
            console.error("Error al copiar: ", err);
        }
    };

    const onSubmit = async (formData: VariableOrderFormData) => {
        let success = false;
        if (formStatus === FormStatusEnum.Add) {
            const { id, ...payload } = formData;
            const resp = await createVariableOrderMutation.mutateAsync({
                ...payload,
                orderType: payload.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA,
                limitType: payload.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE
            });
            success = resp.success;

            if (success) {
                const confirmNumber = (resp.data as unknown as VariableOrderEntity).confirmNumber ?? "";
                showDialog({
                    title: "Se ha creado la orden exitosamente!",
                    description: (
                        <div className="flex-col space-y-6 text-center">
                            <div className="flex text-green-500 font-bold text-6xl justify-center gap-2">
                                {confirmNumber}
                                <ButtonBase onClick={() => handleCopy(confirmNumber)}>
                                    <CopyIcon className="text-primary-500" />
                                </ButtonBase>
                            </div>
                            <p>
                                Es muy importante que utilices este código en la referencia de la orden de mercado ya que así podemos monitorear el cumplimiento de las políticas de inversión y facilitar la asifnación y complementación de la orden!
                            </p>
                        </div>
                    ),
                    variant: "success",
                });
            }
        } else {
            const resp = await updateOrderMutation.mutateAsync({
                id: formData.id ?? "0",
                ...formData,
                orderType: formData.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA,
                limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE
            });
            success = resp.success;
        }
        if (success) {
            await queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
            handleChangeFormStatus(FormStatusEnum.List);
        }
    };

    const onDelete = async (id: string) => {
        await deleteOrderMutation.mutateAsync(id);
        await queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
        handleChangeFormStatus(FormStatusEnum.List);
    };

    const onContinue = async (formData: VariableOrderFormData, newStatus: OrderStatusEnum) => {
        if (orderSelected) {
            if (newStatus === OrderStatusEnum.COMPLEMENTACION) {
                await updateOrderMutation.mutateAsync({
                    id: formData.id ?? "0",
                    ...formData,
                    status: newStatus,
                    orderType: formData.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA,
                    limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
                });
                await queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
                setOrderSelected(prev => ({
                    ...(prev as VariableOrderEntity),
                    status: OrderStatusEnum.COMPLEMENTACION,
                }));
            } else {
                await updateOrderMutation.mutateAsync({
                    id: formData.id ?? "0",
                    ...formData,
                    status: newStatus,
                    orderType: formData.orderType ? OrderTypeEnum.COMPRA : OrderTypeEnum.VENTA,
                    limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
                });
                await queryClient.invalidateQueries({ queryKey: variableOrderKeys.all });
                await setOrderSelected({ ...orderSelected, status: newStatus });
            }

        }
    };

    const handleChangeFormStatus = (statusOrUpdater: FormStatusEnum | ((prevState: FormStatusEnum) => FormStatusEnum)) => {
        const nextStatus = typeof statusOrUpdater === 'function'
            ? (statusOrUpdater as (prev: FormStatusEnum) => FormStatusEnum)(formStatus)
            : statusOrUpdater;
        if (nextStatus === FormStatusEnum.List) {
            setOrderSelected(undefined);
            setInstrumentSelected(undefined);
            setFormKey(prev => prev + 1);
        }

        setFormStatus(nextStatus);
    };

    if (
        isLoading ||
        createVariableOrderMutation.isPending ||
        updateOrderMutation.isPending ||
        deleteOrderMutation.isPending
    ) return <Loading />;

    return (
        <>
            <FormHeader
                title="Renta variable"
                changeFormStatus={handleChangeFormStatus}
                formStatus={formStatus}
                selectItem={setOrderSelected}
                refetch={ordersRefetch}
                canAdd
            />

            {formStatus === FormStatusEnum.List ? (
                <DataTable
                    data={orders}
                    columns={columns}
                    tableHeadClass="bg-primary-500 text-white"
                    pageSize={10}
                    changeFormStatus={setFormStatus}
                    selectItem={setOrderSelected}
                    defaultSortKey="createdAt"
                    defaultSortOrder="desc"
                />
            ) : (
                <VariableOrdersForm
                    onSubmit={onSubmit}
                    onDelete={onDelete}
                    onContinue={onContinue}
                    portfolios={portfolios}
                    portfolioOptions={portfolioOptions}
                    instrumentOptions={instrumentOptions}
                    instrumentSelected={instrumentSelected}
                    setInstrumentSelected={onChange}
                    formStatus={formStatus}
                    orderSelected={orderSelected}
                />
            )}
        </>
    );
};
